import {useNavigate} from 'react-router';
import {Diagram as GoJSDiagram, GraphObject, Rect, InputEvent} from 'gojs';
import {useState} from 'react';

import {APPLICATION_GRAPH_POSITION_STORAGE_KEY} from 'src/constants';
import {ReactDiagram} from 'src/globals';
import {makeNodeAndEdgePositioner} from 'src/util/graph';
import {NODE_RADIUS} from 'src/util/graph/constants';
import {makeInitializer} from 'src/util/gojs';

import {Props} from './types';

import './styles.css';

export const FixedDiagram =
  <T extends {id: string}, U extends {id: string, 0: string, 1: string}>({
    nodes,
    edges,
    height,
    width,
    onLinkClicked,
  }: Props<T, U>) => {
    const navigate = useNavigate();
    const [diagram, setDiagram] = useState<GoJSDiagram | undefined>(undefined);
    const updateDiagram = (diagram?: GoJSDiagram) => {
      setDiagram(diagram);
      if (diagram?.linkTemplateMap && onLinkClicked) {
        const onClicked = (_e: InputEvent, graphObject: GraphObject) => {
          const link = graphObject as go.Link;
          const fromId = link.fromNode?.data.id;
          const toId = link.toNode?.data.id;
          if (fromId && toId) {
            onLinkClicked({
              fromId,
              toId,
            });
          }
        };
        diagram.linkTemplateMap.each((p) => p.value.click = onClicked);
      }
    };

    if (height && width && diagram) {
      diagram.requestUpdate();
      diagram.zoomToRect({
        x: (-width / 2) + NODE_RADIUS,
        y: (-height / 2) + NODE_RADIUS,
        width: width,
        height: height,
      } as Rect); // <- zoomToRect types only accept a heavy-weight Rect object, the vast majority of of whose
    //                properties don't matter for this kind of invocation
    }

    const positioner = makeNodeAndEdgePositioner({height, width});

    const {edges: positionedEdges, nodes: positionedNodes} = positioner({
      nodes,
      edges,
    });

    const initDiagram = makeInitializer({
      onLinkClicked,
      updateDiagram,
      navigate,
    });

    const retrieveOrSetLocation = makeLocationRetriever();
    // TODO: Make this a third stage of transformation, where generic coordinates are
    // converted into GoJS specific data (key, loc)
    const alteredNodes = positionedNodes.map((n) => ({
      ...n,
      key: n.id,
      loc: retrieveOrSetLocation(n),
    }));

    const alteredEdges = positionedEdges.map((e) => ({
      ...e,
      category: 'straight',
      to: '' + e[1],
      from: '' + e[0],
    } as const));

    return (
      <ReactDiagram
        initDiagram={initDiagram}
        divClassName="Diagram__content"
        nodeDataArray={alteredNodes}
        linkDataArray={alteredEdges}
      />
    );
  };

const makeLocationRetriever = () => {
  let storedPositions = {};
  try {
    storedPositions = JSON.parse(localStorage.getItem(APPLICATION_GRAPH_POSITION_STORAGE_KEY) || '{}');
  } catch (e) {
    storedPositions = {};
  }

  return (n: {id: string, x: number, y: number}) =>
    storedPositions[n.id] ?
      `${storedPositions[n.id].x}, ${storedPositions[n.id].y}` :
        `${n.x}, ${n.y}`;
};
